'use client';

import type { APIResponseResultCursor } from '@/types';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * カーソルベースの無限スクロール汎用hook
 *
 * @param initialData SSRで取得した初期データ
 * @param initialHasMore SSRレスポンスの hasMore
 * @param fetcher 次ページを取得する関数。最後のアイテムIDを受け取り、カーソルレスポンスを返す
 */
export function useCursorInfiniteScroll<T extends { id: number }>({
  initialData,
  initialHasMore,
  fetcher,
}: {
  initialData: T[];
  initialHasMore: boolean;
  fetcher: (lastId: number) => Promise<APIResponseResultCursor<T>>;
}) {
  const [data, setData] = useState(initialData);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);

  // initialData が変わったらリセット（フィルター変更時など）
  useEffect(() => {
    setData(initialData);
    setHasMore(initialHasMore);
  }, [initialData, initialHasMore]);

  const dataRef = useRef(data);
  dataRef.current = data;

  const loadMore = useCallback(async () => {
    if (isLoadingRef.current || !hasMore) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    try {
      const lastId = dataRef.current[dataRef.current.length - 1]?.id;
      if (lastId === undefined) return;
      const result = await fetcher(lastId);
      setData((prev) => [...prev, ...result.data]);
      setHasMore(result.hasMore);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [hasMore, fetcher]);

  return { data, hasMore, isLoading, loadMore };
}
